import { produce } from 'immer';

import { ChecklistReadDepth } from 'constants/checklists';

import { RESET_STATE } from 'store/global/actions';
import { CHECKLIST_FULL_ENTITY_ACTION_TYPES, CHECKLISTS_ACTION_TYPE } from 'store/system/actions';

const initialState = {};

const createEmptyEntry = () => ({ depth: ChecklistReadDepth.NONE, nodes: {} });

// The list read is fetched without expression bodies, so a checklist from it carries null
// expressions that the flatten selector renders as ''. A detail form seeded from that shape would
// save the blank over the stored rule, so this records how far each checklist has been re-fetched
// and is what gates the forms.
//
// Two axes, because a node read hydrates ONE node: `depth` is what the checklist-wide reads reached,
// `nodes` is the ids a node read answered for. Depth is a high-water mark, never a downgrade — a
// shallow read after a deep one adds nothing but must not retract the rules already in the store.
const checklistsHydrationReducer = (state = initialState, { type, payload, meta }) =>
  produce(state, draft => {
    switch (type) {
      // A list read WITH expressions hydrates every checklist it returned — but only as deep as it
      // actually went. The default list read asks for neither items nor categories, so claiming ITEMS
      // here would gate off an item form that has no item data behind it.
      case CHECKLISTS_ACTION_TYPE.GET_CHECKLISTS: {
        if (meta?.includeExpressions === false) {
          return initialState;
        }

        Object.keys(payload || {}).forEach(checklistId => {
          draft[checklistId] = {
            depth: meta?.readDepth ?? ChecklistReadDepth.SELF,
            nodes: {},
          };
        });

        break;
      }

      // A node read carries that node's real rule and nothing else, so it hydrates that id alone. The
      // checklist's depth is untouched: its siblings are still whatever the last wider read left them.
      case CHECKLISTS_ACTION_TYPE.GET_CHECKLIST_NODE: {
        const { checklistId, categoryId, itemId } = meta;

        draft[checklistId] ??= createEmptyEntry();
        draft[checklistId].nodes[itemId ?? categoryId] = true;

        break;
      }

      case RESET_STATE:
        return initialState;

      default: {
        if (CHECKLIST_FULL_ENTITY_ACTION_TYPES.has(type)) {
          // A read is only as deep as it asked to be, so it must say. The mutations answer with the
          // whole entity, which is what puts them on this list in the first place. Never infer a depth
          // for a read: over-claiming is what lets a form mount over blanked expressions and save them
          // back, while under-claiming only costs one more read.
          const depth =
            type === CHECKLISTS_ACTION_TYPE.GET_CHECKLIST
              ? (meta?.readDepth ?? ChecklistReadDepth.SELF)
              : ChecklistReadDepth.ITEMS;

          const previous = draft[payload.id] ?? createEmptyEntry();

          draft[payload.id] = {
            depth: Math.max(previous.depth, depth),
            nodes: previous.nodes,
          };

          break;
        }

        return state;
      }
    }
  });

export default checklistsHydrationReducer;
